import { Typography, Box, Button, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { IconButton } from "@mui/material";
import { useModal } from "components/common/Modal";
import { MemberDetails } from "orval/generated/models";
import { getGetAllMembersMembersGetQueryKey } from "orval/generated/endpoint";
import { AxiosError } from "axios";
import { useSnackbar } from "components/common/Notification/showSnackbar";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { CustomIcon, Icons } from "components/common";
import { AXIOS_INSTANCE } from "orval/mutator/custom-instance";

export interface ToggleUserStatusModalProps {
  userInformation: MemberDetails;
}

const deactivateMember = (memberId: number) => {
  return AXIOS_INSTANCE.post(`/members/${memberId}/deactivate`);
};

const activateMember = (memberId: number) => {
  return AXIOS_INSTANCE.post(`/members/${memberId}/activate`);
};

const ToggleUserStatusModal = (props: ToggleUserStatusModalProps) => {
  const { t } = useTranslation("toolConfiguration");
  const { closeModal } = useModal();
  const showSnackbar = useSnackbar();
  const { palette } = useTheme();
  const queryClient = useQueryClient();

  const isCurrentlyActive = props.userInformation.is_active;

  const { mutateAsync: toggleStatus } = useMutation({
    mutationFn: (memberId: number) =>
      isCurrentlyActive ? deactivateMember(memberId) : activateMember(memberId),
    onSuccess: async () => {
      closeModal();
      await queryClient.invalidateQueries({
        queryKey: getGetAllMembersMembersGetQueryKey(),
      });
      showSnackbar(
        isCurrentlyActive
          ? t("userManagementPage.deactivateUser.successMessage")
          : t("userManagementPage.activateUser.successMessage"),
        "success",
      );
    },
    onError: (error: AxiosError) => {
      console.error(error);
      showSnackbar(error as AxiosError, "error");
    },
  });

  return (
    <Box maxWidth={500} data-testid="toggle-user-status-modal">
      <Box marginRight={1} marginTop={1} display="flex" justifyContent="right">
        <IconButton
          aria-label="close"
          sx={{ aspectRatio: 1, float: "right" }}
          onClick={closeModal}
          data-testid="close-toggle-user-status-modal-icon"
        >
          <CustomIcon
            name={Icons.CLOSE}
            width={24}
            height={24}
            fill={palette.text.primary}
          />
        </IconButton>
      </Box>
      <Box paddingX={5} paddingBottom={4}>
        <Typography textAlign="center" variant="h4" marginBottom={3}>
          {isCurrentlyActive
            ? t("userManagementPage.deactivateUser.title")
            : t("userManagementPage.activateUser.title")}
        </Typography>
        <Typography textAlign="center" variant="body2" marginBottom={3}>
          {isCurrentlyActive
            ? t("userManagementPage.deactivateUser.description")
            : t("userManagementPage.activateUser.description")}
        </Typography>
        <Box display="flex" justifyContent="center">
          <Button
            sx={{ mr: 2 }}
            variant="outlined"
            onClick={closeModal}
            data-testid="toggle-user-status-modal-cancel-button"
          >
            {t("common:actions.cancel")}
          </Button>
          <Button
            sx={{ ml: 2 }}
            variant="contained"
            onClick={async () => {
              await toggleStatus(props.userInformation.id);
            }}
            data-testid="toggle-user-status-modal-confirm-button"
          >
            {isCurrentlyActive
              ? t("common:actions.deactivate")
              : t("common:actions.activate")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ToggleUserStatusModal;

